/**
 * 主题 · 亮/暗 + 语言
 * ------------------------------------------------------------
 * 读取 localStorage ema.mode.v1 / ema.lang.v1，写入 data-mode 属性与 <html lang>。
 * 原型对应：EMA_UI 模块 + I18N 模块。
 */
import { ref, watch } from 'vue'
import type { ThemeMode } from '@/design/tokens'
import { useI18n } from '@/design/i18n'
import type { LangCode } from '@/design/i18n'
import { readParam } from '@/shared/query'

const LS_MODE = 'ema.mode.v1'

function detectMode(): ThemeMode {
  /* URL 覆盖优先（演示链接 / 无头验收），但不落库 */
  const ov = readParam('theme')
  if (ov === 'light' || ov === 'dark') return ov
  const saved = localStorage.getItem(LS_MODE) as ThemeMode | null
  if (saved === 'light' || saved === 'dark') return saved
  return (window.matchMedia?.('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light'
}

export const mode = ref<ThemeMode>(detectMode())

/** 初始化：读 localStorage → 应用 data-mode → 读语言 → 应用 lang */
export function initTheme() {
  document.documentElement.setAttribute('data-mode', mode.value)
  document.documentElement.lang = useI18n().lang.value
  watch(mode, m => {
    document.documentElement.setAttribute('data-mode', m)
    localStorage.setItem(LS_MODE, m)
  })
}

export function useTheme() {
  const { setLang } = useI18n()
  function toggleMode() { mode.value = mode.value === 'dark' ? 'light' : 'dark' }
  function setMode(m: ThemeMode) { mode.value = m }
  function setLanguage(l: LangCode) { setLang(l) }
  return { mode, toggleMode, setMode, setLanguage }
}
